import React, { useCallback, useEffect, useState } from "react";
import {
    ArrowRightOutlined,
    LaptopOutlined,
    ReloadOutlined,
    SafetyCertificateOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import { Checkbox, Alert, Card, Col, Empty, Row, Skeleton, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Client } from "../../core/api-client";
import { API_IT_ASSET_LIST } from "../../shared/routes/private.routes";
import DashboardLoadError from "../dashboard/components/dashboard-load-error.component";
import { isFailedResponse } from "../dashboard/dashboard-response";
import {
    buildItDashboardMetrics,
    isSecurityCovered,
    IT_DASHBOARD_STATUSES,
    ItAssetRow,
    ItDashboardMetrics,
    ItStaffItem,
} from "./it-dashboard.metrics";
import "../../assets/styles/dashboard.less";

const { Title, Text } = Typography;

// shortcut: metrics are computed from one list request capped at 2000 staff; move to a dashboard/it-summary endpoint if staff + leavers outgrow it
export const IT_DASHBOARD_ROW_LIMIT = 2000;

type StatusItem = { label: string; value: number; color: string };

const staffColumn = {
    title: "Staff",
    dataIndex: "staffName",
    ellipsis: true,
    render: (_: string, row: ItStaffItem) => (
        <div>
            <strong>{row.staffName || "-"}</strong>
            <div className="dashboard-muted-text">{row.employeeId || "-"}</div>
        </div>
    ),
};

const securityValue = (value?: string | null) =>
    isSecurityCovered(value) ? value : <Tag color="warning">{value || "Not set"}</Tag>;

const securityGapColumns: ColumnsType<ItStaffItem> = [
    staffColumn,
    { title: "Asset Host Name", dataIndex: "assetHostName", ellipsis: true, render: (value) => value || "-" },
    { title: "CrowdStrike", dataIndex: "cs", width: 120, render: securityValue },
    { title: "Action1", dataIndex: "action1", width: 120, render: securityValue },
];

const recoveryColumns: ColumnsType<ItStaffItem> = [
    staffColumn,
    { title: "Status", dataIndex: "staffStatus", width: 130 },
    { title: "Asset Category", dataIndex: "assetCategory", width: 140, render: (value) => value || "-" },
    { title: "O365", dataIndex: "o365", width: 110, render: (value) => value || "-" },
    { title: "Action needed", dataIndex: "reasons", render: (reasons: string[] = []) => reasons.join(" · ") },
];

const ageingColumns: ColumnsType<ItStaffItem> = [
    staffColumn,
    { title: "Model", dataIndex: "model", ellipsis: true, render: (value) => value || "-" },
    { title: "Laptop Year", dataIndex: "laptopYear", width: 120 },
    { title: "Age", dataIndex: "ageYears", width: 100, render: (value: number) => `${value} years` },
];

const ItDashboardPage: React.FC = () => {
    const [metrics, setMetrics] = useState<ItDashboardMetrics>(() =>
        buildItDashboardMetrics([], new Date().getFullYear())
    );
    const [includeInactive, setIncludeInactive] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [truncated, setTruncated] = useState<{ shown: number; total: number } | null>(null);

    const loadDashboard = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const response = await Client.post(
                API_IT_ASSET_LIST,
                { staffStatus: includeInactive ? IT_DASHBOARD_STATUSES : IT_DASHBOARD_STATUSES.filter((status) => status !== "INACTIVE"), includeInactive },
                { params: { page: 1, limit: IT_DASHBOARD_ROW_LIMIT, sort_by: "staffName", sort_type: "ASC" } }
            );
            if (isFailedResponse(response)) {
                setError(true);
                return;
            }
            const rows: ItAssetRow[] = response?.data?.data || [];
            const total = Number(response?.data?.total);
            setMetrics(buildItDashboardMetrics(rows, new Date().getFullYear()));
            setTruncated(total > rows.length ? { shown: rows.length, total } : null);
        } catch (err) {
            console.error("Failed to load IT dashboard:", err);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [includeInactive]);

    useEffect(() => {
        void loadDashboard();
    }, [loadDashboard]);

    const { categories, attention, o365 } = metrics;

    return (
        <div className="app-page dashboard-page">
            <div className="app-page-header dashboard-page-header">
                <div>
                    <div className="app-page-title-wrap">
                        <span className="app-page-icon">
                            <LaptopOutlined />
                        </span>
                        <Title level={1} className="app-page-title">
                            IT Dashboard
                        </Title>
                    </div>
                    <p className="app-page-subtitle">IT asset overview</p>
                </div>

                <Space wrap>
                <Checkbox className="app-include-inactive" checked={includeInactive} onChange={(event) => setIncludeInactive(event.target.checked)}>Include Inactive</Checkbox>
                <button
                    className="dashboard-refresh-button"
                    type="button"
                    onClick={() => void loadDashboard()}
                    disabled={loading}
                    aria-label="Refresh IT dashboard"
                >
                    <ReloadOutlined />
                    <span>Refresh</span>
                </button>
                </Space>
            </div>

            {error ? (
                <DashboardLoadError message="Unable to load IT dashboard data." onRetry={() => void loadDashboard()} />
            ) : (
                <>
                    {truncated && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message={`Showing the first ${truncated.shown} of ${truncated.total} staff. Counts may be incomplete.`}
                        />
                    )}

                    <Row gutter={[16, 16]}>
                        <Col xs={24} lg={12}>
                            <StatusCard
                                title="Current Staff"
                                icon={<LaptopOutlined />}
                                total={metrics.currentStaff}
                                loading={loading}
                                items={[
                                    { label: "Company laptops", value: categories.companyLaptop, color: "success" },
                                    { label: "Personal laptops", value: categories.personal, color: "processing" },
                                    { label: "Client laptops", value: categories.client, color: "processing" },
                                    { label: "No laptop", value: categories.noLaptop, color: "default" },
                                    { label: "No IT record", value: categories.noRecord, color: "warning" },
                                ]}
                            />
                        </Col>
                        <Col xs={24} lg={12}>
                            <StatusCard
                                title="Needs Attention"
                                icon={<WarningOutlined />}
                                loading={loading}
                                items={[
                                    { label: "CrowdStrike gaps", value: attention.crowdStrikeGaps, color: "error" },
                                    { label: "Action1 gaps", value: attention.action1Gaps, color: "error" },
                                    { label: "Laptops 5+ years old", value: attention.ageingLaptops, color: "warning" },
                                    { label: "Laptops to recover", value: attention.assetsToRecover, color: "warning" },
                                    { label: "O365 licences to revoke", value: attention.licencesToRevoke, color: "warning" },
                                ]}
                            />
                        </Col>
                        <Col xs={24} lg={12}>
                            <StatusCard
                                title="O365 Licences"
                                icon={<SafetyCertificateOutlined />}
                                loading={loading}
                                items={[
                                    { label: "Basic", value: o365.basic, color: "processing" },
                                    { label: "Standard", value: o365.standard, color: "processing" },
                                    { label: "No Mail ID", value: o365.noMailId, color: "default" },
                                    { label: "Not set", value: o365.notSet, color: "warning" },
                                ]}
                            />
                        </Col>
                        <Col xs={24} lg={12}>
                            <StatusCard
                                title="Company Laptops by Year"
                                icon={<LaptopOutlined />}
                                loading={loading}
                                items={metrics.laptopYears.map(({ year, count }) => ({
                                    label: year,
                                    value: count,
                                    color: "default",
                                }))}
                            />
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]} className="dashboard-alert-grid">
                        <Col xs={24}>
                            <ItListCard
                                title="Security Gaps"
                                description="Company laptops without CrowdStrike or Action1"
                                data={metrics.securityGaps}
                                columns={securityGapColumns}
                                loading={loading}
                            />
                        </Col>
                        <Col xs={24}>
                            <ItListCard
                                title="Assets to Recover"
                                description="Staff on notice and leavers still holding a laptop or licence"
                                data={metrics.recoveries}
                                columns={recoveryColumns}
                                loading={loading}
                            />
                        </Col>
                        <Col xs={24}>
                            <ItListCard
                                title="Ageing Laptops"
                                description="Company laptops 5 or more years old"
                                data={metrics.ageingLaptops}
                                columns={ageingColumns}
                                loading={loading}
                            />
                        </Col>
                    </Row>
                </>
            )}
        </div>
    );
};

type StatusCardProps = {
    title: string;
    icon: React.ReactNode;
    total?: number;
    loading: boolean;
    items: StatusItem[];
};

function StatusCard({ title, icon, total, loading, items }: StatusCardProps) {
    return (
        <Card className="dashboard-summary-card" variant="borderless">
            {loading ? (
                <Skeleton active paragraph={{ rows: 3 }} />
            ) : (
                <>
                    <div className="dashboard-summary-top">
                        <div className="dashboard-summary-main">
                            <span className="dashboard-summary-icon">{icon}</span>
                            <Text className="dashboard-summary-title">{title}</Text>
                        </div>
                        {total !== undefined && <div className="dashboard-summary-total">{total}</div>}
                    </div>
                    {items.length ? (
                        <div className="dashboard-status-grid">
                            {items.map((item) => (
                                <div className="dashboard-status-item" key={item.label}>
                                    <Tag color={item.color}>{item.label}</Tag>
                                    <strong>{item.value}</strong>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                    )}
                </>
            )}
        </Card>
    );
}

type ItListCardProps = {
    title: string;
    description: string;
    data: ItStaffItem[];
    columns: ColumnsType<ItStaffItem>;
    loading: boolean;
};

function ItListCard({ title, description, data, columns, loading }: ItListCardProps) {
    const [showAll, setShowAll] = useState(false);

    return (
        <Card
            className="dashboard-alert-card"
            variant="borderless"
            title={
                <div className="dashboard-alert-title">
                    <div className="dashboard-alert-title-main">
                        <Space size={8}>
                            <span>{title}</span>
                        </Space>
                        <span className="dashboard-alert-window">{description}</span>
                    </div>
                    {!loading && <Tag color={data.length ? "warning" : "default"}>{data.length}</Tag>}
                </div>
            }
        >
            <Table<ItStaffItem>
                size="small"
                loading={loading}
                rowKey="staffId"
                dataSource={showAll ? data : data.slice(0, 5)}
                columns={columns}
                pagination={false}
                tableLayout="fixed"
                // antd still shows emptyText under the spinner when dataSource is [].
                locale={{
                    emptyText: loading ? null : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No staff found" />,
                }}
            />

            {data.length > 5 && (
                <button type="button" className="dashboard-view-all-button" onClick={() => setShowAll((value) => !value)}>
                    {showAll ? "Show less" : "View all"}
                    <ArrowRightOutlined />
                </button>
            )}
        </Card>
    );
}

export default ItDashboardPage;
